import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStore } from 'common/store';
import { FINAL_REPORT_EMAIL_TEMPLATES } from 'dashboard409a/components/ProcessReportModal/ProcessReportModal.templates';
import type { FinalReportEmailTemplate } from 'dashboard409a/components/ProcessReportModal/ProcessReportModal.types';
import useEmailTemplatePresets from 'dashboard409a/hooks/useEmailTemplatePresets';
import {
  deleteWorkboardEmailTemplatePreset,
  fromWorkboardEmailTemplate,
  getWorkboardEmailTemplatesQueryKey,
  migrateLocalEmailTemplatePresetsIfNeeded,
  saveWorkboardEmailTemplatePreset,
} from 'dashboard409a/hooks/workboardEmailTemplateStorage';
import {
  FINAL_REPORT_EMAIL_TEMPLATE_KEY,
  FINAL_REPORT_EMAIL_TEMPLATE_PRESETS_KEY,
  FINAL_REPORT_EMAIL_TEMPLATES_MIGRATED_KEY,
} from 'dashboard409a/pages/Workboard/common/constants';
import {
  FINAL_REPORT_EMAIL_TEMPLATE_KIND,
  listWorkboardEmailTemplates,
} from 'dashboard409a/services/workboardEmailTemplates';

const pickFinalReportPresetFields = (
  template: FinalReportEmailTemplate
): FinalReportEmailTemplate => ({
  id: template.id,
  label: template.label,
  emailSubject: template.emailSubject,
  introduction: template.introduction,
});

const toFinalReportTemplate = (row: {
  email_subject: string;
  introduction: string;
}): FinalReportEmailTemplate => ({
  id: `preset-server-${row.email_subject}`,
  label: '',
  emailSubject: row.email_subject,
  introduction: row.introduction,
});

export const useFinalReportEmailTemplates = () => {
  const [{ user }] = useStore();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const queryKey = getWorkboardEmailTemplatesQueryKey(userId, FINAL_REPORT_EMAIL_TEMPLATE_KIND);
  const defaults = useMemo(() => FINAL_REPORT_EMAIL_TEMPLATES, []);

  const { data: serverPresets = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      await migrateLocalEmailTemplatePresetsIfNeeded({
        userId: userId as number,
        kind: FINAL_REPORT_EMAIL_TEMPLATE_KIND,
        presetsKey: FINAL_REPORT_EMAIL_TEMPLATE_PRESETS_KEY,
        migrationKey: FINAL_REPORT_EMAIL_TEMPLATES_MIGRATED_KEY,
      });
      const response = await listWorkboardEmailTemplates(FINAL_REPORT_EMAIL_TEMPLATE_KIND);
      return response.definitions.map(row =>
        fromWorkboardEmailTemplate(row, toFinalReportTemplate)
      );
    },
    enabled: userId !== undefined,
    staleTime: 30_000,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey]
  );

  const { mutateAsync: savePresetMutation } = useMutation({
    mutationFn: ({
      name,
      template,
      existingId,
    }: {
      name: string;
      template: FinalReportEmailTemplate;
      existingId?: number;
    }) =>
      saveWorkboardEmailTemplatePreset({
        kind: FINAL_REPORT_EMAIL_TEMPLATE_KIND,
        existingId,
        name,
        template,
      }),
    onSuccess: () => invalidate(),
  });

  const { mutateAsync: deletePresetMutation } = useMutation({
    mutationFn: (presetId: number) => deleteWorkboardEmailTemplatePreset(presetId),
    onSuccess: () => invalidate(),
  });

  const onSavePreset = useCallback(
    async (name: string, template: FinalReportEmailTemplate, existingId?: number) => {
      await savePresetMutation({ name, template, existingId });
    },
    [savePresetMutation]
  );

  const onDeletePreset = useCallback(
    async (_name: string, presetId: number) => {
      await deletePresetMutation(presetId);
    },
    [deletePresetMutation]
  );

  const onUpdatePreset = useCallback(
    async (name: string, template: FinalReportEmailTemplate, presetId: number) => {
      await savePresetMutation({ name, template, existingId: presetId });
    },
    [savePresetMutation]
  );

  const {
    templates,
    presets,
    selectedId,
    selectedTemplate,
    setSelectedId,
    updateSelectedTemplate,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
  } = useEmailTemplatePresets<FinalReportEmailTemplate>({
    activeKey: FINAL_REPORT_EMAIL_TEMPLATE_KEY,
    defaults,
    pickPresetFields: pickFinalReportPresetFields,
    presets: serverPresets,
    onSavePreset,
    onDeletePreset,
    onUpdatePreset,
  });

  const setEmailSubject = useCallback(
    (emailSubject: string) => {
      updateSelectedTemplate({ emailSubject });
    },
    [updateSelectedTemplate]
  );

  const setIntroduction = useCallback(
    (introduction: string) => {
      updateSelectedTemplate({ introduction });
    },
    [updateSelectedTemplate]
  );

  return {
    templates,
    presets,
    selectedId,
    selectedTemplate: selectedTemplate ?? defaults[0] ?? null,
    setSelectedId,
    updateSelectedTemplate,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
    setEmailSubject,
    setIntroduction,
    isLoadingPresets: isLoading,
  };
};

export default useFinalReportEmailTemplates;
